/**
 * Projects a RENDERED form template into numbered sections, with the drafting
 * alternatives each section offers.
 *
 * Not a second document index. `document_text_index.js` projects what a reader
 * sees, for search and annotation, and is right to drop everything hidden. This
 * walks the same DOM for the opposite purpose: to describe what a form OFFERS,
 * which is mostly the part a reader does not see.
 *
 * Why it has to run against a render, and not the template source:
 *
 *   - Section numbering does not exist in the source. It is composed at render
 *     time by the list engine from `ordered_listitem`, and lands in the DOM as
 *     real text (no CSS generated content), so it is only readable afterwards.
 *   - In one shipped office lease: 891 `p[pid]` in the source, 746 in the DOM,
 *     492 of those inside `.lp-hide-element`, and 210 visible and non-empty.
 *     The clauses a form offers outnumber the ones showing by roughly 3:2.
 *
 * That second number is the whole reason this exists. A form does not hold ONE
 * position on the commencement date; it holds three, and a blank form happens to
 * show one of them. Reading only the visible render and calling the result "what
 * your form says" would put a sentence in a customer's mouth that the form never
 * committed to — and it would look right, because the sentence really is in
 * there.
 *
 * `textContent` is not usable here either. Inline alternatives are siblings, so
 * a paragraph offering "a {state} {entity}" or "an individual" reads as
 * "a _______ _______________ an individual" when concatenated. Every text read
 * below therefore excludes the conditional subtrees and collects them
 * separately.
 */

// Content the form is holding in reserve: a clause or phrasing that is real, and
// switched off in this render. The thing this module exists to capture.
const CONDITIONAL_SELECTOR = '.lp-hide-element, .lp-hide';

// Machinery, not prose. `.word-indent` is layout, and the list engine leaves
// control paragraphs like `!!SET_LEVEL_1!!!!Legal2_L2!!` in the flow.
const LAYOUT_ONLY_SELECTOR = '.word-indent';
const CONTROL_MARKER = /^!!.*!!$/;
const ZERO_WIDTH = /[​﻿]/g;

function clean(value) {
  return String(value || '')
    .replace(ZERO_WIDTH, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * One coherent reading of `element`: its own text, minus any conditional
 * subtree nested INSIDE it.
 *
 * The "inside it" is the whole subtlety, and getting it wrong empties the
 * result. A conditional ancestor ABOVE `element` must not disqualify anything —
 * otherwise a hidden paragraph, or an alternative being read on its own, reads
 * as empty, because the nearest conditional is the thing being read.
 */
function readText(element) {
  let text = '';
  const walk = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (parent.closest(LAYOUT_ONLY_SELECTOR)) return NodeFilter.FILTER_REJECT;

      const conditional = parent.closest(CONDITIONAL_SELECTOR);
      const nestedInside =
        conditional && conditional !== element && element.contains(conditional);

      return nestedInside ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
    },
  });

  while (walk.nextNode()) text += walk.currentNode.data;
  return clean(text);
}

/**
 * Everything `element` says, conditional descendants included. What an
 * alternative offers is the whole clause, not the clause minus its own options.
 */
/**
 * Highlighted runs, as the text they cover.
 *
 * In a lease built from a form, a highlight IS a drafter's note. The toolbar
 * applies it with `backColor`, so it arrives as an inline background-color on a
 * span, and firms use it to mark the parts an author still has to resolve —
 * alternatives to choose between, figures to supply, instructions to act on.
 *
 * That makes it a better signal than any wording heuristic: it is the author's
 * own mark, put there deliberately, and it does not depend on guessing which
 * capitalised phrases are imperatives.
 *
 * Returned as strings rather than ranges because the outline is text, and the
 * checks that consume it work in text. The caller finds them again by content.
 */
const HIGHLIGHT_SELECTOR = '[style*="background-color"], [style*="background:"]';
const UNHIGHLIGHTED = /^(?:transparent|none|inherit|initial|rgba?\(0,\s*0,\s*0,\s*0\)|#fff(?:fff)?|white)$/i;

function highlighted(element) {
  return Array.from(element.querySelectorAll(HIGHLIGHT_SELECTOR))
    .filter(node => {
      const colour = (node.style && node.style.backgroundColor) || '';
      return colour.trim() && !UNHIGHLIGHTED.test(colour.trim());
    })
    // A highlight wrapping another highlight is one region. Keeping both would
    // add a range inside a range, which changes nothing and costs a scan.
    .filter(node => !node.parentElement || !node.parentElement.closest(HIGHLIGHT_SELECTOR))
    .map(node => clean(node.textContent))
    .filter(text => text.length > 1);
}

function fullText(element) {
  let text = '';
  const walk = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      return parent.closest(LAYOUT_ONLY_SELECTOR)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT;
    },
  });

  while (walk.nextNode()) text += walk.currentNode.data;
  return clean(text);
}

/**
 * Each conditional region inside `element`, as its own complete reading.
 *
 * Outermost only, and read in full. The list engine puts its switch on an outer
 * element and repeats the marker on descendants — a `lease-if` region arrives as
 * `.lp-hide.lp-hide-element` wrapping further `.lp-hide-element` spans — so a
 * region is one alternative wearing several markers, not alternatives nested
 * inside alternatives. Reading it minus its own descendants returned an empty
 * string for every region in a real form.
 */
function alternatives(element) {
  return Array.from(element.querySelectorAll(CONDITIONAL_SELECTOR))
    .filter(node => {
      const enclosing = node.parentElement.closest(CONDITIONAL_SELECTOR);
      // Outermost within `element`: nothing conditional between it and
      // `element`, where `element` itself does not count as "between".
      return (
        !enclosing || enclosing === element || !element.contains(enclosing)
      );
    })
    .map(fullText)
    .filter(Boolean);
}

function isControlMarker(text) {
  return CONTROL_MARKER.test(text);
}

// The composed number and the words after it, e.g. "1." + "Basic Lease
// Information." or "1.03" + "“Term”: Zero (0) full calendar months". They arrive
// fused because the list engine writes the number as ordinary text into the same
// paragraph, and a classifier reading "8.Alterations" as a title has to strip it
// anyway.
// Two numbering styles, both real: "8.Alterations" and
// "ARTICLE 19- MUTUAL WAIVER OF SUBROGATION". Only the first was recognised, so
// every heading in a form using the second kept its number glued to its title
// and reported `number: null`.
// The letter suffix is not decoration. Forms insert an article without
// renumbering the sixty that follow it, so "ARTICLE 1A" sits between 1 and 2 —
// and reading it as "ARTICLE 1" gives two different sections the same number
// while the orphaned "A" leads the title.
const LEADING_NUMBER = /^\s*(?:(ARTICLE|SECTION|EXHIBIT)\s+([\dIVXL]+[A-Z]?)\s*[-–—.:]?|(\d+(?:\.\d+)*\.?))\s*/i;

function splitNumber(text) {
  const match = LEADING_NUMBER.exec(text);
  if (!match) return { number: null, title: text };

  const number = match[1] ? `${match[1].toUpperCase()} ${match[2]}` : match[3];
  return { number: number, title: text.slice(match[0].length).trim() };
}

/**
 * Outline depth, 0 being the shallowest.
 *
 * `data-list-set-level` holds `SET_LEVEL_0` … `SET_LEVEL_4`, not an integer, so
 * `Number()` returns NaN and every section silently collapses into one. A
 * paragraph with no attribute is body text under whatever section is open.
 */
function outlineLevel(paragraph) {
  const raw = paragraph.getAttribute('data-list-set-level');
  const match = /(\d+)\s*$/.exec(raw || '');
  return match ? Number(match[1]) : null;
}

/**
 * @param {Document|Element} root a RENDERED form document
 * @returns {{sections: Array, stats: Object}}
 */
export function buildFormOutline(root) {
  const scope = root.querySelector ? root : document;
  const container = scope.querySelector('.lease') || scope;
  const paragraphs = Array.from(container.querySelectorAll('p[pid]'));

  // Computed first, because whether a paragraph opens a section depends on the
  // document as a whole rather than on what preceded it.
  const levels = paragraphs.map(outlineLevel).filter(level => level !== null);
  const shallowestLevel = levels.length ? Math.min(...levels) : null;

  const sections = [];
  const stats = {
    paragraphs: 0,
    conditional: 0,
    withAlternatives: 0,
    controlMarkers: 0,
    skippedEmpty: 0,
  };

  let current = null;

  paragraphs.forEach(paragraph => {
    const text = readText(paragraph);
    const options = alternatives(paragraph);

    if (isControlMarker(text)) {
      stats.controlMarkers += 1;
      return;
    }
    if (!text && !options.length) {
      stats.skippedEmpty += 1;
      return;
    }

    // A paragraph the form is not showing. Kept, and marked — the reviewer is
    // being told what the form CAN say, and a clause switched off by default is
    // still a clause the form offers.
    const conditional = Boolean(paragraph.closest('.lp-hide-element'));
    const level = outlineLevel(paragraph);
    const list = paragraph.getAttribute('data-list-set-heading') || null;

    stats.paragraphs += 1;
    if (conditional) stats.conditional += 1;
    if (options.length) stats.withAlternatives += 1;

    // Only the shallowest level in the document opens a section. Everything
    // deeper is body text belonging to the section above it.
    //
    // This was `level <= current.level`, which meant two consecutive paragraphs
    // at the SAME depth each opened one — so at the deeper levels every clause
    // became its own section and its own title. A real form came back with
    // `sectionTitle` set to a wall of clause text, and in three cases identical
    // to the `formPosition` quoted from it.
    //
    // It matters beyond the label. Design §8 asks whether a section is SILENT
    // on a concept, and a "section" one clause long cannot demonstrate silence
    // about anything. Over-splitting quietly destroys the property the whole
    // extractor rests on.
    const opensSection = level !== null && level === shallowestLevel;

    if (opensSection) {
      const heading = splitNumber(text);
      current = {
        level: level,
        list: list,
        number: heading.number,
        title: heading.title,
        pid: paragraph.getAttribute('pid'),
        conditional: conditional,
        paragraphs: [],
      };
      sections.push(current);
      return;
    }

    if (current === null) {
      current = {
        level: null,
        list: null,
        number: null,
        title: null,
        pid: null,
        conditional: false,
        paragraphs: [],
      };
      sections.push(current);
    }

    current.paragraphs.push({
      pid: paragraph.getAttribute('pid'),
      text: text,
      // What the author has marked as still to resolve. Carried so the checks
      // can tell a defect from an alternative nobody has chosen yet.
      notes: highlighted(paragraph),
      alternatives: options,
      conditional: conditional,
      level: level,
    });
  });

  stats.sections = sections.length;
  return { sections: sections, stats: stats };
}

